import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

/**
 * Lists all lineup validation issues (both teams), grouped by team. Opened from
 * the footer eye icon in the copilot view. Errors are objects of the shape
 * { team, severity: 'error' | 'warning', message }.
 */
const LineupErrorsDialog = ({ open, onClose, errors = [] }) => {
  const teams = [...new Set(errors.map((e) => e.team))];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          color: '#fff',
          borderRadius: 2,
          borderTop: '6px solid #feec67',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography sx={{ fontWeight: 700, letterSpacing: 1, fontSize: 16 }}>LINEUP CHECKS</Typography>
        <Box
          component="button"
          onClick={onClose}
          aria-label="Close"
          sx={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 22, lineHeight: 1, px: 0.5 }}
        >
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {errors.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CheckCircleRoundedIcon sx={{ color: '#3ddc84', fontSize: 22 }} />
            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
              No lineup issues — both teams are good to go.
            </Typography>
          </Box>
        ) : (
          teams.map((team) => (
            <Box key={team} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', mb: 0.75 }}>
                {team}
              </Typography>
              {errors.filter((e) => e.team === team).map((e, i) => {
                const isError = e.severity === 'error';
                const color = isError ? '#ff6b6b' : '#feec67';
                const Icon = isError ? ErrorRoundedIcon : WarningAmberRoundedIcon;
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Icon sx={{ color, fontSize: 20, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, lineHeight: 1.3, color: 'rgba(255,255,255,0.9)' }}>
                      {e.message}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
};

LineupErrorsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      team: PropTypes.string,
      severity: PropTypes.oneOf(['error', 'warning']),
      message: PropTypes.string,
    }),
  ),
};

export default LineupErrorsDialog;
